import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, forkJoin, map } from 'rxjs';
import { environment } from '@environments/environment';
import { switchMap, take, filter, withLatestFrom } from 'rxjs/operators';
import { IResponseSuccess } from '@core/interfaces';
import { Store } from '@ngrx/store';
import { selectAvailabilityList, selectLocalChannelId, selectProductList, selectStore, selectSuggestionItems } from '@store/selectors';
import { selectCart } from '@store/selectors/cart.selectors';
import { Availability, MenuItem, Store as StoreModel } from '@core/models';
import { IntegerPrecisionService } from '../integer-precision/integer-precision.service';
import { menuItemProcessor } from '@/core/domain/menu';
import { StoreService } from '../store/store.service';
import { CartService } from '../cart/cart.service';

@Injectable({
	providedIn: 'root',
})
export class MenuItemService {
	store$: Observable<StoreModel | null>;
	menuItemList$: Observable<MenuItem[] | null>;
	menuItem$: Observable<MenuItem | null>;
	availabilityList$: Observable<Availability[] | null>;

	constructor(
		private http: HttpClient,
		private store: Store,
		private integerPrecisionService: IntegerPrecisionService,
		private storeService: StoreService,
		private cartService: CartService,

	) {
		this.store$ = this.store.select(selectStore);
		this.menuItemList$ = this.store.select(selectProductList);
		this.menuItem$ = this.store.select(selectCart);
		this.availabilityList$ = this.store.select(selectAvailabilityList);
	}

	getProducts(): Observable<MenuItem[]> {
		return this.menuItemList$.pipe(
			withLatestFrom(
				this.store.select(selectLocalChannelId),
				this.cartService.getStoreForStore(),
			),
			filter(([menuItemList, localChannelId]) => !!menuItemList && !!localChannelId),
			map(([menuItemList, _, cart]) => {
				return (
					menuItemList?.map((product: MenuItem) => {
						const cartItem = cart?.find(c => c.idCartItem == product.idCartItem);
						const cantItemsInCart = cart?.filter(c => c.idArticulo == product.idArticulo)?.length || 0;

						if (cartItem) return { ...cartItem, cantItemsInCart };
						else return { ...product, cantItemsInCart };
					}) ?? []
				);
			})
		);
	}

	getMenuItem(id?: string | null): Observable<IResponseSuccess<MenuItem>> {
		return this.store$.pipe(
			withLatestFrom(this.store.select(selectLocalChannelId)),
			filter(([store, channelId]) => !!id && !!store && !!channelId),
			switchMap(([store, channelId]) => {
				const params = {
					...(!!channelId && { useLocalCanalId: channelId }),
				};

				return this.http.get<IResponseSuccess<MenuItem>>(`${environment.microConfiguraciones}/articulo/${id}`, {
					params: {
						...params,
					},
					headers: {
						authorization: store!.apiKey,
					},
				});
			}),
			take(1)
		);
	}

	getMenuItemList(
		suggestionItems?: string[] | null,
		productName?: string | null,
		subcategoryId?: string | null,
		localChannelId?: string | null,
		menuId?: string | null
	): Observable<IResponseSuccess<MenuItem>> {
		return this.store$.pipe(
			filter(store => store !== null),
			switchMap(store => {
				const params = {
					...(subcategoryId && subcategoryId !== 'all' && { subcategoriaId: subcategoryId }),
					...(localChannelId &&
						localChannelId !== 'all' && { localCanalId: localChannelId, useLocalCanalId: localChannelId }),
					...(productName && { nombre: productName }),
					...(menuId && { menuId }),
					...(suggestionItems && { suggestItems: suggestionItems }),
					localId: store.idLocal,
				};

				return this.http.get<IResponseSuccess<MenuItem>>(`${environment.microConfiguraciones}/articulo`, {
					params,
					headers: {
						authorization: store.apiKey,
					},
				});
			}),
			take(1)
		)
	}

	updateStock(
		id: string,
		cantidad: number,
		operacion: 'INCREMENTO' | 'DECREMENTO'
	): Observable<IResponseSuccess<MenuItem>> {
		return this.store$.pipe(
			filter(store => store !== null),
			switchMap(store => {
				return this.http.patch<IResponseSuccess<MenuItem>>(
					`${environment.microConfiguraciones}/articulo/stock/${id}`,
					{
						cantidad,
						operacion,
					},
					{
						headers: {
							authorization: store.apiKey,
						},
					}
				);
			}),
			take(1)
		);
	}

	updateAllStock(cartItems: MenuItem[]): Observable<IResponseSuccess<MenuItem>[]> {
		const calls: Observable<IResponseSuccess<MenuItem>>[] = [];

		cartItems.forEach(item => {
			calls.push(this.updateStock(item.idArticulo, Number(item?.cantidad ?? 0n), 'DECREMENTO'));
		});
		return forkJoin(calls);
	}

	getSuggestionItems(): Observable<MenuItem[]> {
		return this.store.select(selectSuggestionItems)
			.pipe(
				withLatestFrom(this.availabilityList$),
				map(([suggestionItems, availabilityList]) => {
					return menuItemProcessor.processCatalog(
						suggestionItems ?? [],
						availabilityList ?? [],
						this.integerPrecisionService,
						new Date()
					)
				}
				)
			)
	}

	updateAllCart(cartItemList: MenuItem[]): Observable<IResponseSuccess<MenuItem>[]> {
		const calls: Observable<IResponseSuccess<MenuItem>>[] = [];

		cartItemList.forEach((cartItem) => {
			calls.push(this.getMenuItem(cartItem?.idArticulo));
		})

		return forkJoin(calls);
	}
}
